import { useState, useRef, useEffect, useContext, useMemo } from "react";
import { NavLink, useLocation, Link, useNavigate } from "react-router-dom";
import {
  MdOutlineDashboard,
  MdOutlineAdminPanelSettings,
  MdKeyboardArrowDown,
  MdKeyboardArrowUp,
  MdSearch,
  MdClose,
  MdShoppingCart,
} from "react-icons/md";
import { BsChatSquareText } from "react-icons/bs";
import { FaBars } from "react-icons/fa";
import { CiUser } from "react-icons/ci";
import { tokenDecode } from "../../utils/helper";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { useSidebar } from "../../context/SidebarContext";
import { HiOutlineQuestionMarkCircle } from "react-icons/hi2";
import { IoHomeOutline } from "react-icons/io5";
import { TbFileInvoice } from "react-icons/tb";
import { TokenContext } from "../../context/TokenContext";
import { useProfile } from "../../context/ProfileContext";
import { useToken } from "../../hooks/token/useToken";
import logoimg from "../../icons/image_2024.png";
import jwt_decode from "jwt-decode";
import { httpService } from "../../services/httpService";
import { toast } from "react-toastify";

const MobileNavbar = ({ setIsMobile, isMobile }) => {
  const [show, setShow] = useState(false);
  const [isDropDown, setIsDropDown] = useState(false);
  const [isAdminDropDown, setIsAdminDropDown] = useState(false);
  const [isReportDropDown, setIsReportDropDown] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isTablet, setIsTablet] = useState(
    window.innerWidth >= 768 && window.innerWidth < 1280,
  );
  const { decodedData, isFreePlan, isHomePlan, isShopPlan, updateToken } =
    useContext(TokenContext);
  const adminRef = useRef(null);
  const reportRef = useRef(null);
  const profileRef = useRef(null);
  const location = useLocation();

  const navigate = useNavigate();
  const { showSidebar, setShowSidebar, toggleSidebar } = useSidebar();
  const { isEmployee } = useToken();
  const { menuPermissions = [], profileData, rawServiceCategory } = useProfile();
  const [planBannerDismissed, setPlanBannerDismissed] = useState(false);

  const vendorSubscriptionPlans = profileData?.subscriptionPlan;
  const currentPlan = vendorSubscriptionPlans?.find(
    (p) => p.status == 1 || p.status == "active",
  );

  const planWarning = useMemo(() => {
    if (!currentPlan || !currentPlan.end_date || !currentPlan.start_date) {
      return null;
    }

    const now = new Date();
    const start = new Date(currentPlan.start_date);
    const end = new Date(currentPlan.end_date);

    if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;

    const totalDays = Math.round((end - start) / (1000 * 60 * 60 * 24));
    const diffDays = Math.max(
      0,
      Math.ceil((end - now) / (1000 * 60 * 60 * 24)),
    );

    if (diffDays <= 0) return null;

    const isFreeTrial = totalDays >= 20 && totalDays <= 22;

    if (isFreeTrial) {
      return {
        days: diffDays,
        isFreeTrial: true,
        headline: `${diffDays} ${diffDays === 1 ? "day" : "days"} left in your free trial`,
        subtext: "Choose a plan any time to keep your account active",
        cta: "Choose a plan",
      };
    }

    if (diffDays <= 5) {
      return {
        days: diffDays,
        isFreeTrial: false,
        headline: `${diffDays} ${diffDays === 1 ? "day" : "days"} left in your plan`,
        subtext: "Choose a plan any time to keep your account active",
        cta: "Choose a plan",
      };
    }

    return null;
  }, [currentPlan]);

  const decoded = tokenDecode();
  const data = decodedData?.data || decoded?.data || {};
  const currentRole =
    data?.user?.role_data?.name || data?.user?.role_type || "unknown";

  const [searchTerm, setSearchTerm] = useState("");

  // change password

  // Add these states near other useState declarations
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const isShopOnly = useMemo(() => {
    if (!rawServiceCategory || rawServiceCategory.length === 0) return false;
    const isPetShopOrPosCategory = (cat) => {
      const name = cat.service_category_name?.toLowerCase() || "";
      return name === "pet shop" || name === "pos";
    };
    const nonShop = rawServiceCategory.filter(
      (cat) => !isPetShopOrPosCategory(cat),
    );
    return nonShop.length === 0;
  }, [rawServiceCategory]);

  const validatePassword = (password) => {
    if (password.length < 8) return "Password must be at least 8 characters";
    if (!/[A-Z]/.test(password))
      return "Must contain at least one uppercase letter";
    if (!/\d/.test(password)) return "Must contain at least one number";
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password))
      return "Must contain at least one special character";
    return null;
  };

  const resetPasswordForm = () => {
    setPasswordData({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
    setErrors({});
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setErrors({});

    const newPassError = validatePassword(passwordData.newPassword);
    if (newPassError) {
      setErrors({ newPassword: newPassError });
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setErrors({ confirmPassword: "Passwords do not match" });
      return;
    }

    setLoading(true);

    try {
      await httpService.post("/user/changePassword", {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
        confirmPassword: passwordData.confirmPassword,
      });

      toast.success("Password changed successfully!");
      setShowChangePassword(false);
      resetPasswordForm();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to change password");
    } finally {
      setLoading(false);
    }
  };

  // Handle responsive breakpoints
  useEffect(() => {
    const handleResize = () => {
      setIsTablet(window.innerWidth >= 768 && window.innerWidth < 1280);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      const trimmedTerm = searchTerm.trim();
      if (trimmedTerm) {
        navigate(`/search_clients?search=${encodeURIComponent(trimmedTerm)}`);
        setIsSearchOpen(false);
        setSearchTerm("");
      }
    }
  };

  const allowedByName = (menuName) => {
    

    try {
      if (!menuPermissions || menuPermissions.length === 0) {
        return true;
      }
      const permission = menuPermissions.find((p) => p.menu_name === menuName);
      return permission ? permission.is_active : true;
    } catch {
      return true;
    }
  };

  useEffect(() => {
    if (isMobile) {
      setShowSidebar(false);
    }
  }, [isMobile]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        adminRef.current &&
        !adminRef.current.contains(event.target) &&
        reportRef.current &&
        !reportRef.current.contains(event.target)
      ) {
        setIsAdminDropDown(false);
        setIsReportDropDown(false);
      }

      // Close profile dropdown when clicking outside
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setIsDropDown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const logouthandler = () => {
    localStorage.removeItem("us_st_d");
  };

  const adminPaths = [
    "/admin/settings/general_settings",
    "/services",
    "/admin/inventory-management",
    "/employees",
    "/client_pets",
    "/admin/finance",
    "/admin/account-setup",
    "/admin/reports",
    "/calendar_setting",
  ];

  const isInAdminSection = adminPaths.some((path) =>
    location.pathname.startsWith(path),
  );

  const handleRoleSwitch = async (role) => {
    const originalToken = localStorage.getItem("us_st_d");
    const originalRole = localStorage.getItem("originalRole");
    const currentRole = jwt_decode(originalToken).data.user.role_data.name;

    if (role === "admin" && originalRole !== "admin") {
      toast.error("You are not allowed to switch to Admin");
      return;
    }

    // if (role === currentRole) {
    //   updateToken(originalToken);
    //   localStorage.removeItem("employee_tokens");
    //   toast.success(`Switched back to original role: ${role}`);
    //   navigate(role === "executive" ? "/home" : "/dashboard");
    //   return;
    // }
    saveRoleToken(currentRole, originalToken);

    const cachedToken = getRoleToken(role);
    if (cachedToken) {
      updateToken(cachedToken);
      toast.success(`Switched to ${role} role`);
      navigate(role === "executive" ? "/home" : "/dashboard");
      return;
    }

    try {
      const res = await httpService.post("/user/switchrole", { role });
      const newToken = res.data?.token;

      if (!newToken) {
        toast.error("No token received from server");
        return;
      }

      // Cache token for this role
      saveRoleToken(role, newToken);

      // Update TokenContext
      updateToken(newToken);
      toast.success(`Switched to ${role} role`);
      navigate(role === "executive" ? "/home" : "/dashboard");
    } catch (error) {
      console.error("Role switch error:", error);
      toast.error(error.response?.data?.message || "Failed to switch role");
    }
  };

  const saveRoleToken = (role, token) => {
    const tokens = JSON.parse(localStorage.getItem("cachedToken") || "{}");
    tokens[role] = token;
    localStorage.setItem("cachedToken", JSON.stringify(tokens));
  };

  const getRoleToken = (role) => {
    const tokens = JSON.parse(localStorage.getItem("cachedToken") || "{}");
    return tokens[role];
  };

  const canSwitchToAdmin = () => {
    const originalRole = localStorage.getItem("originalRole");
    const cachedAdminToken = getRoleToken("admin");

    return originalRole === "admin" || !!cachedAdminToken;
  };

  const showSwitchToAdmin = currentRole !== "admin";
  const showSwitchToExecutive = currentRole !== "executive";
  // Debug - remove after testing
 

  return (
    <div className="fixed top-0 left-0 right-0 z-50 flex flex-col w-full bg-white shadow-md border-b border-gray-100">
      {/* GLOBAL PLAN / TRIAL EXPIRY NOTIFICATION BANNER (CONTAINED & CENTERED ABOVE MOBILE NAVBAR) */}
      {planWarning && !planBannerDismissed && !isEmployee && currentRole !== "executive" && (
        <div className="w-[calc(100%-24px)] md:w-[75%] max-w-[850px] mx-auto my-1.5 bg-[#0D7A70] text-white min-h-[38px] px-3 sm:px-4 py-1.5 rounded-xl shadow-md flex items-center justify-between gap-2 text-xs border border-teal-600/30">
          <div className="flex items-center gap-1.5 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap">
            <svg
              className="w-4 h-4 flex-shrink-0 text-teal-200"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
            >
              <circle cx="12" cy="12" r="9" />
              <path strokeLinecap="round" d="M12 7v5l3 2" />
            </svg>
            <span className="font-bold flex-shrink-0">
              {planWarning.headline}
            </span>
            <span className="text-teal-200/80 hidden sm:inline flex-shrink-0 font-bold">·</span>
            <span className="text-teal-100 truncate min-w-0 hidden sm:inline">
              {planWarning.subtext}
            </span>
          </div>

          <div className="flex items-center gap-1.5 flex-shrink-0">
            <button
              onClick={() => {
                navigate("/admin/settings/profile");
              }}
              className="bg-white text-[#0D7A70] hover:bg-teal-50 text-xs font-semibold px-2.5 py-1 rounded-lg transition-all whitespace-nowrap cursor-pointer shadow-xs"
            >
              {planWarning.cta}
            </button>

            <button
              onClick={() => setPlanBannerDismissed(true)}
              className="p-1 text-teal-200 hover:text-white hover:bg-white/10 rounded-lg transition-colors cursor-pointer"
              aria-label="Dismiss notification"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* MOBILE NAVBAR ROW */}
      <div className="w-full flex justify-between items-center px-4 lg:px-6 py-3 min-h-[64px] gap-2">
      {/* Search Overlay Mode */}
      {isSearchOpen ? (
        <div className="absolute inset-0 bg-white z-50 flex items-center px-4 gap-2 shadow-lg">
          <input
            type="text"
            placeholder="Search client..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={handleKeyDown}
            autoFocus
            className="w-full flex-1 py-2.5 px-4 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none transition-all"
          />
          <button
            onClick={() => {
              setIsSearchOpen(false);
              setSearchTerm("");
            }}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-full transition-colors cursor-pointer"
          >
            <MdClose size={24} />
          </button>
        </div>
      ) : (
        <>
          {/* Left: Sidebar Toggle Button + Logo */}
          <div className="flex items-center gap-2">
            {!isEmployee && currentRole !== "executive" && (
              <button
                type="button"
                onClick={toggleSidebar}
                aria-label="Toggle Sidebar"
                className="p-2 rounded-lg text-gray-700 hover:text-[#1B4582] hover:bg-gray-100 transition-colors focus:outline-none flex xl:hidden items-center justify-center cursor-pointer"
                title="Toggle Sidebar"
              >
                <FaBars className="text-xl text-gray-700" />
              </button>
            )}
            <img
              src={logoimg}
              alt="Logo"
              className="w-32 md:w-36 object-contain"
            />
          </div>

          {/* Right: Icons (Search icon, Profile, Hamburger) */}
          <div className="flex items-center gap-2 md:gap-3">
            {/* Search Icon (opens search overlay on click) */}
            <button
              onClick={() => setIsSearchOpen(true)}
              className="p-2 text-gray-600 hover:bg-blue-100/50 rounded-full transition-colors cursor-pointer"
              title="Search clients"
              aria-label="Search clients"
            >
              <MdSearch size={26} />
            </button>

            {/* Profile Dropdown (All Mobile/Tablet) */}
            <div className="relative" ref={profileRef}>
              <button
                className="flex items-center gap-1 p-1.5 rounded-full hover:bg-blue-100/50 transition-colors"
                onClick={() => setIsDropDown(!isDropDown)}
              >
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-white flex items-center justify-center font-semibold text-sm shadow-md">
                  {typeof data?.vendor?.profile?.name === "string" &&
                  data.vendor.profile.name.length > 0
                    ? data.vendor.profile.name[0].toUpperCase()
                    : "U"}
                </div>
                {isTablet && (
                  <>
                    {isDropDown ? (
                      <MdKeyboardArrowUp className="text-gray-600" size={20} />
                    ) : (
                      <MdKeyboardArrowDown
                        className="text-gray-600"
                        size={20}
                      />
                    )}
                  </>
                )}
              </button>

              {/* Profile Dropdown Menu */}
              {isDropDown && (
                <div className="absolute right-0 top-14 w-56 bg-white border border-gray-200 rounded-xl shadow-2xl z-50 overflow-hidden">
                  <div className="flex items-center px-4 py-3 bg-gradient-to-r from-blue-50 to-blue-100 border-b gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-white flex items-center justify-center font-semibold shadow-md">
                      {typeof data?.vendor?.profile?.name === "string" &&
                      data.vendor.profile.name.length > 0
                        ? data.vendor.profile.name[0].toUpperCase()
                        : "U"}
                    </div>
                    <div className="flex flex-col text-sm">
                      <span className="font-semibold text-gray-800">
                        {data?.vendor?.profile?.name || "User"}
                      </span>
                      <span className="text-xs text-gray-500 capitalize">
                        {currentRole}
                      </span>
                    </div>
                  </div>

                  <Link
                    to="/profile"
                    className="block px-4 py-2.5 text-gray-700 hover:bg-blue-50 transition-colors"
                    onClick={() => setIsDropDown(false)}
                  >
                    Profile
                  </Link>

                  {/* Change Password Button */}
                  <button
                    onClick={() => {
                      setIsDropDown(false);
                      setShowChangePassword(true);
                    }}
                    className="block w-full px-4 py-2.5 text-left text-gray-700 hover:bg-blue-50 transition-colors"
                  >
                    Change Password
                  </button>

                  {showSwitchToAdmin &&
                    canSwitchToAdmin() &&
                    currentRole !== "admin" && (
                      <button
                        onClick={() => handleRoleSwitch("admin")}
                        className="block w-full px-4 py-2.5 text-left text-gray-800 hover:bg-blue-50 transition"
                      >
                        Switch to Admin
                      </button>
                    )}

                  {showSwitchToExecutive && (
                    <button
                      onClick={() => handleRoleSwitch("executive")}
                      className="block w-full px-4 py-2.5 text-left text-gray-800 hover:bg-blue-50 transition"
                    >
                      Switch to Executive
                    </button>
                  )}

                  <Link
                    to="/profilesettings"
                    className="block px-4 py-2.5 text-gray-700 hover:bg-blue-50 transition-colors"
                    onClick={() => setIsDropDown(false)}
                  >
                    Settings
                  </Link>

                  <Link
                    to="#"
                    onClick={() => {
                      logouthandler();
                      setIsDropDown(false);
                    }}
                    className="block px-4 py-2.5 text-red-600 hover:bg-red-50 border-t transition-colors"
                  >
                    Logout
                  </Link>
                </div>
              )}

              {/* ==================== CHANGE PASSWORD DRAWER ==================== */}
              {showChangePassword && (
                <div className="fixed inset-0 z-[100] flex justify-end">
                  {/* Backdrop */}
                  <div
                    className="absolute inset-0 bg-black bg-opacity-50"
                    onClick={() => {
                      setShowChangePassword(false);
                      resetPasswordForm();
                    }}
                  />

                  {/* Drawer Panel */}
                  <div className="relative w-full max-w-md bg-white h-full shadow-2xl overflow-y-auto">
                    <div className="p-6">
                      {/* Header */}
                      <div className="flex items-center justify-between mb-6">
                        <h2 className="text-2xl font-semibold text-gray-800">
                          Change Password
                        </h2>
                        <button
                          onClick={() => {
                            setShowChangePassword(false);
                            resetPasswordForm();
                          }}
                          className="text-3xl text-gray-400 hover:text-gray-600"
                        >
                          ✕
                        </button>
                      </div>

                      {/* Form */}
                      <form
                        onSubmit={handleChangePassword}
                        className="space-y-6"
                      >
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Current Password
                          </label>
                          <input
                            type="password"
                            value={passwordData.currentPassword}
                            onChange={(e) =>
                              setPasswordData((prev) => ({
                                ...prev,
                                currentPassword: e.target.value,
                              }))
                            }
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:border-[#3BD82E]"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            New Password
                          </label>
                          <input
                            type="password"
                            value={passwordData.newPassword}
                            onChange={(e) =>
                              setPasswordData((prev) => ({
                                ...prev,
                                newPassword: e.target.value,
                              }))
                            }
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:border-[#3BD82E]"
                            required
                          />
                          {errors.newPassword && (
                            <p className="text-red-500 text-xs mt-1">
                              {errors.newPassword}
                            </p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Confirm New Password
                          </label>
                          <input
                            type="password"
                            value={passwordData.confirmPassword}
                            onChange={(e) =>
                              setPasswordData((prev) => ({
                                ...prev,
                                confirmPassword: e.target.value,
                              }))
                            }
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:border-[#3BD82E]"
                            required
                          />
                          {errors.confirmPassword && (
                            <p className="text-red-500 text-xs mt-1">
                              {errors.confirmPassword}
                            </p>
                          )}
                        </div>

                        <button
                          type="submit"
                          disabled={loading}
                          className="w-full bg-[#5D5CE8] hover:bg-[#7776f2] text-white py-3.5 rounded-xl font-medium transition disabled:opacity-70 mt-4"
                        >
                          {loading ? "Updating Password..." : "Update Password"}
                        </button>
                      </form>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Hamburger / Menu (Far Right)
            <button
              onClick={() => setShow(!show)}
              className="p-2.5 focus:outline-none text-gray-700 hover:bg-blue-100/50 rounded-full transition-colors"
            >
              {show ? <MdClose size={26} /> : <FaBars className="text-2xl" />}
            </button> */}
          </div>
        </>
      )}

      {/* Backdrop */}
      {show && (
        <div
          className="fixed inset-0 top-[64px] bg-black/30 backdrop-blur-sm z-[40]"
          onClick={() => setShow(false)}
        />
      )}

      {/* Hamburger Drawer Menu */}
      {show && (
        <div className="absolute top-[64px] right-0 w-full bg-white shadow-2xl z-50 border-t border-gray-200 rounded-bl-xl overflow-hidden">
          <ul className="flex flex-col text-black max-h-[calc(100vh-80px)] overflow-y-auto">
            {/* Main Navigation Items */}
            {currentRole === "executive" && (
              <li className="w-full hover:bg-blue-50 transition-colors">
                <NavLink
                  to="/home"
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-6 py-3.5 ${
                      isActive
                        ? "bg-gradient-to-r from-blue-100 to-blue-50 text-blue-600 font-semibold border-l-4 border-blue-600"
                        : ""
                    }`
                  }
                  onClick={() => setShow(false)}
                >
                  <IoHomeOutline size={22} />
                  <span className="text-sm">Home</span>
                </NavLink>
              </li>
            )}

            {currentRole === "executive" && (
              <li className="w-full hover:bg-blue-50 transition-colors">
                <NavLink
                  to="/admin/order-management/orders"
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-6 py-3.5 ${
                      isActive
                        ? "bg-gradient-to-r from-blue-100 to-blue-50 text-blue-600 font-semibold border-l-4 border-blue-600"
                        : ""
                    }`
                  }
                  onClick={() => setShow(false)}
                >
                  <MdShoppingCart size={22} />
                  <span className="text-sm">Orders</span>
                </NavLink>
              </li>
            )}

            {allowedByName("Calendar") && (
              <li className="w-full hover:bg-blue-50 transition-colors">
                <NavLink
                  to="/calendar"
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-6 py-3.5 ${
                      isActive
                        ? "bg-gradient-to-r from-blue-100 to-blue-50 text-blue-600 font-semibold border-l-4 border-blue-600"
                        : ""
                    }`
                  }
                  onClick={() => setShow(false)}
                >
                  <img
                    src={require("../../icons/calendar.png")}
                    alt="cal"
                    className="h-5 w-5"
                  />
                  <span className="text-sm">Calendar</span>
                </NavLink>
              </li>
            )}

            {allowedByName("Invoice") && (
              <li className="w-full hover:bg-blue-50 transition-colors">
                <NavLink
                  to="/invoice"
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-6 py-3.5 ${
                      isActive
                        ? "bg-gradient-to-r from-blue-100 to-blue-50 text-blue-600 font-semibold border-l-4 border-blue-600"
                        : ""
                    }`
                  }
                  onClick={() => setShow(false)}
                >
                  <TbFileInvoice size={22} />
                  <span className="text-sm">Invoice</span>
                </NavLink>
              </li>
            )}

            <li className="w-full hover:bg-blue-50 transition-colors">
              <NavLink
                to="/faq"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-6 py-3.5 ${
                    isActive
                      ? "bg-gradient-to-r from-blue-100 to-blue-50 text-blue-600 font-semibold border-l-4 border-blue-600"
                      : ""
                  }`
                }
                onClick={() => setShow(false)}
              >
                <HiOutlineQuestionMarkCircle size={24} />
                <span className="text-sm">FAQ</span>
              </NavLink>
            </li>

            {/* Dashboard & Chat */}
            {/* {[
              {
                to: "/dashboard",
                icon: <MdOutlineDashboard size={22} />,
                label: "Dashboard",
                show: true,
              },
              {
                to: "/chatwithcustomer",
                icon: <BsChatSquareText size={20} />,
                label: "Chat with us",
                show: true,
              },
            ]
              .filter((item) => item.show)
              .map(({ to, icon, label }) => (
                <li
                  key={to}
                  className="w-full hover:bg-blue-50 transition-colors"
                >
                  <NavLink
                    to={to}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-6 py-3.5 ${
                        isActive
                          ? "bg-gradient-to-r from-blue-100 to-blue-50 text-blue-600 font-semibold border-l-4 border-blue-600"
                          : ""
                      }`
                    }
                    onClick={() => setShow(false)}
                  >
                    {icon}
                    <span className="text-sm">{label}</span>
                  </NavLink>
                </li>
              ))} */}

            {/* <li className="border-t border-gray-200 my-2"></li> */}

            {/* Report Dropdown */}
            {/* <li className="relative" ref={reportRef}>
              <button
                onClick={() => setIsReportDropDown(!isReportDropDown)}
                className={`w-full flex items-center justify-between px-6 py-3.5 hover:bg-blue-50 transition-colors ${
                  location.pathname.startsWith("/report")
                    ? "bg-gradient-to-r from-blue-100 to-blue-50 text-blue-600 border-l-4 border-blue-600"
                    : ""
                }`}
              >
                <span className="flex items-center gap-3">
                  <MdOutlineDashboard size={22} />
                  <span className="text-sm font-medium">Report</span>
                </span>
                {isReportDropDown ? (
                  <MdKeyboardArrowUp size={20} />
                ) : (
                  <MdKeyboardArrowDown size={20} />
                )}
              </button>
              {isReportDropDown && (
                <ul className="bg-gradient-to-r from-blue-50/50 to-gray-50 pb-2">
                  {["services", "products", "inventory", "work"].map((item) => (
                    <li key={item}>
                      <NavLink
                        to={`/report/${item}`}
                        className={({ isActive }) =>
                          `block px-12 py-2.5 hover:text-blue-600 hover:bg-white text-sm transition-colors ${
                            isActive
                              ? "text-blue-600 font-semibold bg-white"
                              : "text-gray-700"
                          }`
                        }
                        onClick={() => setShow(false)}
                      >
                        {item[0].toUpperCase() + item.slice(1)}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              )}
            </li> */}

            {/* Admin Dropdown */}
            {/* <li className="relative" ref={adminRef}>
              <button
                onClick={() => setIsAdminDropDown(!isAdminDropDown)}
                className={`w-full flex items-center justify-between px-6 py-3.5 hover:bg-blue-50 transition-colors ${
                  isInAdminSection
                    ? "bg-gradient-to-r from-blue-100 to-blue-50 text-blue-600 border-l-4 border-blue-600"
                    : ""
                }`}
              >
                <span className="flex items-center gap-3">
                  <MdOutlineAdminPanelSettings size={22} />
                  <span className="text-sm font-medium">Admin</span>
                </span>
                {isAdminDropDown ? (
                  <MdKeyboardArrowUp size={20} />
                ) : (
                  <MdKeyboardArrowDown size={20} />
                )}
              </button>
              {isAdminDropDown && (
                <ul className="bg-gradient-to-r from-blue-50/50 to-gray-50 pb-2">
                  {[
                    {
                      to: "/admin/settings/online_profile",
                      label: "Online Profile",
                    },
                    {
                      to: "/admin/service-management/grooming",
                      label: "Service Management",
                    },
                    {
                      to: "/admin/inventory-management",
                      label: "Inventory Management",
                    },
                    { to: "/employees", label: "Staff Management" },
                    { to: "/client_pets", label: "Clients/Pets" },
                    { to: "/admin/finance", label: "Finance" },
                    { to: "/admin/account-setup", label: "Account Setup" },
                    { to: "/admin/reports", label: "Reports" },
                    { to: "/calendar_setting", label: "Settings" },
                  ].map(({ to, label }) => (
                    <li key={to}>
                      <NavLink
                        to={to}
                        className={({ isActive }) =>
                          `block px-12 py-2.5 hover:text-blue-600 hover:bg-white text-sm transition-colors ${
                            isActive
                              ? "text-blue-600 font-semibold bg-white"
                              : "text-gray-700"
                          }`
                        }
                        onClick={() => setShow(false)}
                      >
                        {label}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              )}
            </li> */}
          </ul>
        </div>
      )}
    </div>
  </div>
);
};;

export default MobileNavbar;
